"use client";

import {
  Stack,
  Box,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  FormLabel,
  Select,
  Button,
} from "@chakra-ui/react";
import "services/Recruit/assets/css/CreateJobOpening/style.css";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { globalStyles } from "common/theme/styles";
import { Field, Formik, Form } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { constant } from "common/constant";
import ActionNavbar from "common/components/actionBar/ActionNavbar";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_JOB_OPENING_INITIAL_VALUES } from "common/initialValues/formInitialValues";
import { toast } from "react-toastify";
import { getApi, postApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { putApi } from "common/services/api";
import { departmentTabsData } from "services/Recruit/data/tabsData";
import { DepartmentSchema } from "common/schema/department/departmentSchema";
import Spinner from "common/components/spinner/Spinner";
import TextEditor from "common/components/textEditor/TextEditor";
import { departmentOptions } from "services/Recruit/data/departmentOptions";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";

const DepartmentCreate = () => {
  const colors = globalStyles.colors;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const { id } = useParams();
  const [jobData, setJobData] = useState(null);
  const { addLoading } = useSelector((state) => state.departments);
  const [editLoading, setEditLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [permission] = HasAccess([accessModuleTitles.Departments]);

  const handleTabChange = (index) => {
    setActiveTabIndex(index);
  };

  useEffect(() => {
    if (id) {
      const fetchTaskData = async () => {
        try {
          const result = await getApi(API_PATHS.RECRUITMENT_DEPARTMENTS, id);
          setJobData(result?.data);
        } catch (e) {
          toast.error(constant.somethingWrong);
        }
      };

      fetchTaskData();
    }
  }, [id]);

  const EditData = async (values) => {
    try {
      setEditLoading(true);
      const response = await putApi(
        `${API_PATHS.RECRUITMENT_DEPARTMENTS}${id}`,
        values
      );
      if (response.status === 200) {
        toast.success("Department updated successfully!");
        navigate("/organization/departments");
      }
      if (response?.status === 400) {
        toast.error(response?.data?.message || "Department already exists!");
      }
    } catch (e) {
      // Handle 400 status for department already exists during edit
      toast.error(constant.somethingWrong);
    } finally {
      setEditLoading(false);
    }
  };

  const CreateData = async (values, { resetForm }) => {
    try {
      setCreateLoading(true);
      const response = await postApi(API_PATHS.RECRUITMENT_DEPARTMENTS, values);
      if (response.status === 200 || response.status === 201) {
        toast.success("Department created successfully!");
        resetForm();
        navigate("/organization/departments");
      }
    } catch (e) {
      // Handle 400 status for department already exists during create
      if (e.response?.status === 400) {
        toast.error(e.response?.data?.message || "Department already exists!");
      } else {
        toast.error(constant.somethingWrong);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <ActionNavbar isCreatePage={true} />
      <Stack>
        <Box className="createJobOpening-container">
          <Box className="createJobOpening-mainContainer">
            <Box className="createJobOpening-breadCrumb">
              <ArrowBackIcon
                onClick={() => navigate("/organization/departments")}
                fontSize="20px"
                cursor={"pointer"}
              />
              <Text className="createJobOpening-pageTitle">
                {id ? constant.editDepartment : constant.createDepartment}
              </Text>
            </Box>
          </Box>

          <Formik
            enableReinitialize
            initialValues={
              jobData
                ? {
                  ...jobData,
                  department: jobData.department?._id || "",
                }
                : CREATE_JOB_OPENING_INITIAL_VALUES
            }
            validationSchema={DepartmentSchema}
            onSubmit={(values, formikBag) => {
              const departmentData = id
                ? {
                  // Only send description when editing (name is disabled)
                  description: values.description,
                }
                : {
                  // Send both name and description when creating
                  name: values.name,
                  description: values.description,
                }

              if (id) {
                EditData(departmentData) // Pass departmentData instead of values for consistency
              } else {
                CreateData(departmentData, formikBag)
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors, dirty, isValid }) => (
              <Form onSubmit={handleSubmit}>
                <Tabs
                  index={activeTabIndex}
                  onChange={handleTabChange}
                  className="createJobOpening-tabsContainer"
                  variant="unstyled"
                  orientation="vertical"
                >
                  {/* tab list  */}
                  <TabList w={"40%"} className="createJobOpening-tabListContainer">
                    {departmentTabsData.map(({ label, Icon }, index) => (
                      <Tab
                        className="createJobOpening-tabs"
                        key={index}
                        _selected={{
                          color: colors.cta["100"],
                          borderLeft: "2px solid",
                          borderColor: colors.cta["100"],
                        }}
                        _hover={{
                          color: colors.cta["100"],
                        }}
                        _focus={{ boxShadow: "none" }}
                        display="flex"
                        alignItems="center"
                        gap="8px"
                      >
                        <Icon size={20} />
                        {label}
                      </Tab>
                    ))}
                  </TabList>

                  {/* tab panels */}
                  <TabPanels w={"60%"} className="createJobOpening-tabsPanelContainer">
                    {/* department tab */}
                    <TabPanel className="createJobOpening-tabsPanel">
                      <Box className="createJobOpening-tabsPanelBody">
                        <Flex gap={"20px"}>
                          <Box w={"100%"}>
                            <FormLabel htmlFor="name">
                              {constant.nameLabel}
                              <span className="astrick">*</span>
                            </FormLabel>
                            <Field name="name">
                              {({ field, meta }) => {
                                return (
                                  <>
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      placeholder={constant.selectDepartment || "Select Department"}
                                      borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                      isDisabled={!!id} // Conditionally disable: enabled for create, disabled for edit
                                      opacity={id ? 0.6 : 1} // Visual indication when disabled
                                      cursor={id ? "not-allowed" : "pointer"}
                                      backgroundColor={id ? colors.gray["50"] : "white"} // Different background when disabled
                                    >
                                      {departmentOptions.map((option) => (
                                        <option key={option.value} value={option.label}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </Select>
                                    {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    {id && (
                                      <Text fontSize="sm" color={colors.red["500"]} mt={1}>
                                        Department name cannot be changed while editing
                                      </Text>
                                    )}
                                  </>
                                )
                              }}
                            </Field>
                          </Box>
                        </Flex>

                        <Box w={"100%"}>
                          <Text className="textEditorHeading">
                            {constant.description}
                          </Text>
                          <Field name="description">
                            {({ field, meta }) => (
                              <>
                                <TextEditor
                                  isDisabled={!permission?.update}
                                  toolbarId="departmentDescription"
                                  value={field.value || ""}
                                  handleChange={(content) =>
                                    field.onChange?.({
                                      target: {
                                        name: "description",
                                        value: content,
                                      },
                                    })
                                  }
                                  handleBlur={() =>
                                    field.onBlur?.({
                                      target: { name: "description" },
                                    })
                                  }
                                  borderColor={
                                    meta.touched && meta.error
                                      ? colors.errorText
                                      : colors.borderColor
                                  }
                                  errorMsg={
                                    meta.touched && meta.error ? meta.error : ""
                                  }
                                />
                                {meta.touched && meta.error && (
                                  <Text color={colors.errorText}>
                                    {meta.error}
                                  </Text>
                                )}
                              </>
                            )}
                          </Field>
                        </Box>
                      </Box>

                      <Box className="createJobOpening-tabsPanelFooter">
                        <Button
                          className="theme-btn2 cancelBtn"
                          sx={{
                            textTransform: "capitalize",
                          }}
                          variant="outline"
                          colorScheme={colors.error["100"]}
                          size="sm"
                          onClick={() => navigate("/organization/departments")}
                        >
                          {constant.cancel}
                        </Button>
                        <Button
                          disabled={
                            // Include all loading states and form validation
                            addLoading ||
                            editLoading ||
                            createLoading ||
                            !isValid ||
                            !dirty ||
                            !permission?.update
                          }
                          className="nextBtn"
                          variant="primary"
                          type="submit"
                        >
                          {addLoading || editLoading || createLoading ? (
                            <Spinner size="sm" />
                          ) : id ? (
                            constant.update
                          ) : (
                            constant.save
                          )}
                        </Button>
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Form>
            )}
          </Formik>
        </Box>
      </Stack>
    </>
  );
};

export default DepartmentCreate;
